import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Receipt, AlertCircle, ExternalLink } from 'lucide-react';
import { StatusBadge } from '../../components/StatusBadge';
import { KpiGrid, SectionHeader, ExportButtons, ReportTable, fIDR, fIDRCompact, fDateID } from './shared';

export function TaxTab({ data, loading, filters }) {
  const navigate = useNavigate();
  const s = data?.summary || {};
  const cards = [
    { label: 'Total DPP', value: fIDRCompact(s.total_dpp), accent: '' },
    { label: 'Total DPP Nilai Lain', value: fIDRCompact(s.total_dpp_nilai_lain), accent: '' },
    { label: 'Total PPN', value: fIDRCompact(s.total_ppn), accent: 'primary' },
    { label: 'Total Grand Total', value: fIDRCompact(s.total_grand), accent: '' },
    { label: 'Total PPh Dipotong', value: fIDRCompact(s.total_pph), accent: 'warn' },
    { label: 'Invoice Dengan PPN', value: s.invoices_with_ppn || 0, accent: 'primary' },
    { label: 'Invoice Dengan PPh', value: s.invoices_with_pph || 0, accent: 'warn' },
    { label: 'Bukti Potong Waiting', value: s.bukti_potong_waiting || 0, accent: 'danger' },
    { label: 'Bukti Potong Received', value: s.bukti_potong_received || 0, accent: 'success' },
  ];
  const rows = data?.rows || [];
  const waiting = data?.waiting_bukti_potong || [];

  return (
    <div className="space-y-4">
      <SectionHeader icon={Receipt} title="Laporan Pajak" action={<ExportButtons tab="tax" filters={filters} hasPdf />} />
      <KpiGrid items={cards} loading={loading} columns={4} />

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Detail Pajak per Invoice ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.id}
            columns={[
              { key: 'invoice_number', label: 'No Invoice', render: r => <span className="font-mono-num text-slate-100">{r.invoice_number}</span> },
              { key: 'invoice_date', label: 'Tgl Inv', render: r => <span className="text-slate-300">{fDateID(r.invoice_date)}</span> },
              { key: 'client_name', label: 'Client', render: r => <span className="font-medium text-slate-100">{r.client_name}</span> },
              { key: 'dpp_amount', label: 'DPP', align: 'right', render: r => <span className="font-mono-num text-slate-200">{fIDR(r.dpp_amount)}</span> },
              { key: 'dpp_nilai_lain_amount', label: 'DPP Nilai Lain', align: 'right', render: r => <span className="font-mono-num text-slate-300">{fIDR(r.dpp_nilai_lain_amount)}</span> },
              { key: 'ppn_amount', label: 'PPN', align: 'right', render: r => <span className="font-mono-num text-primary">{fIDR(r.ppn_amount)}</span> },
              { key: 'grand_total_amount', label: 'Grand Total', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.grand_total_amount)}</span> },
              { key: 'withholding_tax_type', label: 'PPh Type', render: r => <span className="text-xs text-slate-300">{r.withholding_tax_type || '—'}</span> },
              { key: 'withholding_tax_rate', label: 'Rate', align: 'right', render: r => <span className="text-xs text-slate-300">{r.withholding_tax_rate ? `${r.withholding_tax_rate}%` : '—'}</span> },
              { key: 'withholding_tax_amount', label: 'PPh Amount', align: 'right', render: r => <span className="font-mono-num text-amber-300">{fIDR(r.withholding_tax_amount)}</span> },
              { key: 'net_amount_to_collect', label: 'Net Collect', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.net_amount_to_collect)}</span> },
              { key: 'withholding_status', label: 'Status BP', render: r => <span className={`text-xs font-medium ${r.withholding_status === 'Waiting' ? 'text-amber-300' : r.withholding_status === 'Received' ? 'text-emerald-300' : 'text-slate-400'}`}>{r.withholding_status === 'Not Required' ? '—' : r.withholding_status}</span> },
              { key: 'bukti_potong_number', label: 'No BP', render: r => <span className="text-xs text-slate-400 font-mono-num">{r.bukti_potong_number || '—'}</span> },
              { key: 'bukti_potong_date', label: 'Tgl BP', render: r => <span className="text-xs text-slate-400">{r.bukti_potong_date ? fDateID(r.bukti_potong_date) : '—'}</span> },
              { key: 'action', label: 'Aksi', align: 'right', render: r => (
                <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${r.id}`); }}><ExternalLink className="h-3 w-3" /></Button>
              ) },
            ]}
            rows={rows}
            emptyText="Belum ada data pajak pada periode ini."
            mobileCard={(r) => (
              <div className="text-sm space-y-1" onClick={() => navigate(`/invoices/${r.id}`)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{r.client_name}</div>
                    <div className="text-xs text-slate-300 font-mono-num">{r.invoice_number}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-300 mt-2">
                  <div>DPP: <span className="font-mono-num text-slate-100">{fIDR(r.dpp_amount)}</span></div>
                  <div>PPN: <span className="font-mono-num text-primary">{fIDR(r.ppn_amount)}</span></div>
                  <div>PPh: <span className="font-mono-num text-amber-300">{fIDR(r.withholding_tax_amount)}</span></div>
                  <div>Net: <span className="font-mono-num text-slate-100">{fIDR(r.net_amount_to_collect)}</span></div>
                </div>
                <div className="text-xs text-slate-300">Status BP: <span className={r.withholding_status === 'Waiting' ? 'text-amber-300 font-medium' : 'text-slate-400'}>{r.withholding_status === 'Not Required' ? '—' : r.withholding_status}</span></div>
              </div>
            )}
          />
        </CardContent>
      </Card>

      {waiting.length > 0 && (
        <Card className="bg-card border-amber-400/30">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-200 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Bukti Potong Belum Diterima ({waiting.length})</CardTitle></CardHeader>
          <CardContent className="p-0 sm:p-4">
            <ReportTable
              getRowKey={(r) => `wbp-${r.id}`}
              columns={[
                { key: 'invoice_number', label: 'No Invoice', render: r => <span className="font-mono-num text-slate-100">{r.invoice_number}</span> },
                { key: 'client_name', label: 'Client', render: r => <span className="text-slate-100">{r.client_name}</span> },
                { key: 'withholding_tax_amount', label: 'PPh Amount', align: 'right', render: r => <span className="font-mono-num text-amber-300">{fIDR(r.withholding_tax_amount)}</span> },
                { key: 'net_amount_to_collect', label: 'Net Collect', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.net_amount_to_collect)}</span> },
                { key: 'withholding_status', label: 'Status', render: r => <span className="text-xs text-amber-300">{r.withholding_status}</span> },
                { key: 'action', label: 'Aksi', align: 'right', render: r => (
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${r.id}`); }}><ExternalLink className="h-3 w-3" /></Button>
                ) },
              ]}
              rows={waiting}
              emptyText="Semua bukti potong telah diterima."
              mobileCard={(r) => (
                <div className="text-sm space-y-1" onClick={() => navigate(`/invoices/${r.id}`)}>
                  <div className="font-semibold text-slate-100">{r.client_name}</div>
                  <div className="text-xs text-slate-300 font-mono-num">{r.invoice_number}</div>
                  <div className="grid grid-cols-2 gap-1 text-xs mt-1">
                    <div>PPh: <span className="font-mono-num text-amber-300">{fIDR(r.withholding_tax_amount)}</span></div>
                    <div>Net: <span className="font-mono-num text-slate-100">{fIDR(r.net_amount_to_collect)}</span></div>
                  </div>
                </div>
              )}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
